import { db } from "@/indexedDB/db";
import { touchAttachmentAccess } from "@/lib/storage/accessRecency";
import {
  FileAttachmentEntity,
  MediaAttachmentEntity,
  VoiceAttachmentEntity,
} from "../types";
import {
  storeAttachmentContent,
  retrieveAttachmentContent,
  deleteAttachmentContent,
  clearAllAttachmentContent,
} from "@/lib/storage";

// Materialize a detached copy of a row-read thumbnail before it escapes to a
// caller. An IDB-read Blob is a lazy HANDLE into the row's blob storage, and
// rewriting that row later (content landing, eviction's pointer clear) kills
// the handle retroactively on WebKit — the row-29 broken-"?" class. Thumbnails
// are tens of KB, so the copy is cheap. Content blobs are deliberately NOT
// copied (they can be ~100 MB) — they are liveness-guarded at serve time
// instead.
const materializeThumbnail = async (thumbnail: Blob): Promise<Blob> => {
  try {
    return new Blob([await thumbnail.arrayBuffer()], { type: thumbnail.type });
  } catch {
    // Copy failed — a handle whose backing already died, or a Blob-shaped
    // value without arrayBuffer (fake-indexeddb's structured clone in tests).
    // Serve the original: downstream liveness guards degrade a dead handle to
    // a cache miss, which must stay a miss rather than become a throw here.
    return thumbnail;
  }
};

// ---------------- //
// MEDIA ATTACHMENTS //
// ---------------- //

// Add media attachment with hybrid storage support
export const addMediaAttachmentToLocalDB = async (
  attachment: MediaAttachmentEntity,
) => {
  let entityToStore = { ...attachment };

  // Route large content to OPFS
  if (attachment.content) {
    const { storedIn, blob } = await storeAttachmentContent(
      attachment.id,
      attachment.content,
    );
    touchAttachmentAccess(attachment.id);
    entityToStore = {
      ...entityToStore,
      content: blob ?? undefined,
      contentStoredIn: storedIn,
      // Fresh content = fresh eviction rank (see updateMediaAttachmentContent).
      lastAccessedAt: Date.now(),
    };
  }

  // MERGE with any existing row instead of replacing it. The content and
  // thumbnail of one attachment are fetched by INDEPENDENT concurrent paths
  // (separate single-flight keys), and each caller builds a row carrying only
  // its own field from a read taken before the other's write. A bare put()
  // made the last writer win — losing either the just-cached thumbnail or the
  // content/contentStoredIn pointer (orphaning the OPFS file and forcing a
  // re-download). The transaction serialises read-merge-write against the
  // sibling call. `undefined` values in the incoming entity must not erase
  // populated fields on the existing row, so they're stripped before merging.
  return await db.transaction("rw", db.mediaAttachments, async () => {
    const existing = await db.mediaAttachments.get(entityToStore.id);
    if (!existing) {
      return db.mediaAttachments.put(entityToStore);
    }
    const definedEntries = Object.fromEntries(
      Object.entries(entityToStore).filter(([, v]) => v !== undefined),
    );
    return db.mediaAttachments.put({
      ...existing,
      ...definedEntries,
    } as MediaAttachmentEntity);
  });
};

// Update media attachment content with hybrid storage support
export const updateMediaAttachmentContent = async (
  id: string,
  field: "thumbnail" | "content",
  content: Blob,
) => {
  // Thumbnails always go to IndexedDB
  if (field === "thumbnail") {
    return await db.mediaAttachments.update(id, { [field]: content });
  }

  // For content, route through hybrid storage. The write refreshes the
  // recency marker too — without it a re-downloaded video kept its original
  // (old) eviction rank and was re-evicted by the very next store (thrash).
  const { storedIn, blob } = await storeAttachmentContent(id, content);
  touchAttachmentAccess(id);
  return await db.mediaAttachments.update(id, {
    content: blob ?? undefined,
    contentStoredIn: storedIn,
    lastAccessedAt: Date.now(),
  });
};

// Remove media attachment by id => attachmentId
export const removeMediaAttachmentFromLocalDB = async (
  attachmentId: string,
) => {
  // Delete from OPFS if stored there
  await deleteAttachmentContent(attachmentId);
  return await db.mediaAttachments.delete(attachmentId);
};

// Bulk add media attachment list
export const addMediaAttachmentsToLocalDB = async (
  attachmentList: MediaAttachmentEntity[],
) => {
  return await db.mediaAttachments.bulkPut(attachmentList);
};

// Get paginated media attachments by chatId with a limit
export const getPaginatedMediaAttachmentsFromLocalDB = async (
  chatId: string,
  limit: number,
) => {
  // First get attachments by chatId, then sort them by timestamp
  // We can't use orderBy directly because there's no compound index for chatId+timestamp
  return await db.mediaAttachments
    .where("chatId")
    .equals(chatId)
    .limit(limit)
    .sortBy("timestamp")
    .then((attachments) => [...attachments].reverse()); // newest first
};

// Get total media attachment count by chatId
export const getMediaAttachmentCountByChatIdFromLocalDB = async (
  chatId: string,
) => {
  return await db.mediaAttachments
    .where("chatId")
    .equals(chatId)
    .count()
    .then((count) => count);
};

// Get media attachment by id => attachmentId
export const getMediaAttachmentFromLocalDB = async (attachmentId: string) => {
  const entity = await db.mediaAttachments.get(attachmentId);
  if (!entity?.thumbnail) {
    return entity;
  }
  // Every path that hands a row-read thumbnail to a caller must vend a copy
  // (see materializeThumbnail) — callers hold these past the next row rewrite
  // (e.g. the edit-draft hydration keeps one for the whole editing session).
  return {
    ...entity,
    thumbnail: await materializeThumbnail(entity.thumbnail),
  };
};

// Get media attachment content (retrieves from correct storage)
export const getMediaAttachmentContentFromLocalDB = async (
  attachmentId: string,
  field: "thumbnail" | "content" = "content",
): Promise<Blob | null> => {
  const entity = await db.mediaAttachments.get(attachmentId);
  if (!entity) {
    return null;
  }

  // Thumbnails are always in IndexedDB — vended as a materialized COPY, never
  // the row's file-backed handle (see materializeThumbnail).
  if (field === "thumbnail") {
    return entity.thumbnail
      ? await materializeThumbnail(entity.thumbnail)
      : null;
  }

  // Recency touch for the eviction policy — MEMORY ONLY. Writing it to the
  // row rewrote the whole record (IDB has no partial update) and killed the
  // file-backed Blob handles already on screen from this row — the broken-"?"
  // images / dead-URL videos regression. Reads must never write. Only content
  // reads count: thumbnail reads happen for every bubble and would mark
  // unwatched content as "in use".
  if (entity.content || entity.contentStoredIn === "opfs") {
    touchAttachmentAccess(attachmentId);
  }

  // Content may be in OPFS or IndexedDB. Pass fileType so an OPFS-sourced blob
  // (empty MIME type) gets re-typed — otherwise iOS <video> rejects the cached
  // video with MEDIA_ERR_SRC_NOT_SUPPORTED ([cache E4]).
  return await retrieveAttachmentContent(
    attachmentId,
    entity.content,
    entity.contentStoredIn,
    entity.fileType,
  );
};

// ---------------- //
// FILE ATTACHMENTS //
// ---------------- //

// Add file attachment with hybrid storage support
export const addFileAttachmentToLocalDB = async (
  attachment: FileAttachmentEntity,
) => {
  let entityToStore = { ...attachment };

  // Route large content to OPFS
  if (attachment.content) {
    const { storedIn, blob } = await storeAttachmentContent(
      attachment.id,
      attachment.content,
    );
    touchAttachmentAccess(attachment.id);
    entityToStore = {
      ...entityToStore,
      content: blob ?? undefined,
      contentStoredIn: storedIn,
      // Fresh content = fresh eviction rank, same as the media write paths —
      // without it a just-downloaded file kept its old rank and was the very
      // next sweep's first victim (thrash).
      lastAccessedAt: Date.now(),
    };
  }

  return await db.fileAttachments.put(entityToStore);
};

// Remove file attachment by id => attachmentId
export const removeFileAttachmentFromLocalDB = async (attachmentId: string) => {
  // Delete from OPFS if stored there
  await deleteAttachmentContent(attachmentId);
  return await db.fileAttachments.delete(attachmentId);
};

// Bulk add file attachment list
export const addFileAttachmentsToLocalDB = async (
  attachmentList: FileAttachmentEntity[],
) => {
  return await db.fileAttachments.bulkPut(attachmentList);
};

// Get file attachments by chatId
export const getFileAttachmentsFromLocalDB = async (chatId: string) => {
  return await db.fileAttachments
    .where("chatId")
    .equals(chatId)
    .reverse()
    // TO DO => check if custom index needed for better query perf (infinite scroll + virtual list)
    .sortBy("timestamp");
};

// Get file attachment by id => attachmentId
export const getFileAttachmentFromLocalDB = async (attachmentId: string) => {
  return await db.fileAttachments.get(attachmentId);
};

// Get file attachment content (retrieves from correct storage)
export const getFileAttachmentContentFromLocalDB = async (
  attachmentId: string,
): Promise<Blob | null> => {
  const entity = await db.fileAttachments.get(attachmentId);
  if (!entity) {
    return null;
  }

  // Recency touch for the eviction policy — memory only (see the media
  // reader above: a row-write here killed on-screen blob handles).
  if (entity.content || entity.contentStoredIn === "opfs") {
    touchAttachmentAccess(attachmentId);
  }

  // Content may be in OPFS or IndexedDB. Pass fileType so an OPFS-sourced blob
  // (empty MIME type) is re-typed on read (see media reader above).
  return await retrieveAttachmentContent(
    attachmentId,
    entity.content,
    entity.contentStoredIn,
    entity.fileType,
  );
};

// Get paginated file attachments by chatId with a limit
export const getPaginatedFileAttachmentsFromLocalDB = async (
  chatId: string,
  limit: number,
) => {
  return await db.fileAttachments
    .where("chatId")
    .equals(chatId)
    .reverse() // newest first
    .limit(limit)
    .sortBy("timestamp");
};

// Get total file attachment count by chatId
export const getFileAttachmentCountByChatIdFromLocalDB = async (
  chatId: string,
) => {
  return await db.fileAttachments
    .where("chatId")
    .equals(chatId)
    .count()
    .then((count) => count);
};

// ---------------- //
// VOICE ATTACHMENTS //
// ---------------- //

// Add voice attachment
export const addVoiceAttachmentToLocalDB = async (
  attachment: VoiceAttachmentEntity,
) => {
  return await db.voiceAttachments.put(attachment);
};

// Get voice attachment
export const getVoiceAttachmentFromLocalDB = async (id: string) => {
  return await db.voiceAttachments.get(id);
};

// Remove voice attachment
export const removeVoiceAttachmentFromLocalDB = async (id: string) => {
  return await db.voiceAttachments.delete(id);
};

// Bulk add voice attachment list
export const addVoiceAttachmentsToLocalDB = async (
  attachmentList: VoiceAttachmentEntity[],
) => {
  return await db.voiceAttachments.bulkPut(attachmentList);
};

// Get voice attachments by chatId
export const getVoiceAttachmentsByChatIdFromLocalDB = async (
  chatId: string,
) => {
  return await db.voiceAttachments
    .where("chatId")
    .equals(chatId)
    .reverse()
    // TO DO => check if custom index needed for better query perf (infinite scroll + virtual list)
    .sortBy("timestamp");
};

// Get paginated voice attachments by chatId with a limit
export const getPaginatedVoiceAttachmentsFromLocalDB = async (
  chatId: string,
  limit: number,
) => {
  return await db.voiceAttachments
    .where("chatId")
    .equals(chatId)
    .reverse() // newest first
    .limit(limit)
    .sortBy("timestamp");
};

// Get  voice attachment count by chatId
export const getVoiceAttachmentCountByChatIdFromLocalDB = async (
  chatId: string,
) => {
  return await db.voiceAttachments
    .where("chatId")
    .equals(chatId)
    .count()
    .then((count) => count);
};

// STORAGE CLEANUP  //
// ---------------- //

// Clear all attachment content from OPFS (used in storage settings)
export const clearAllAttachmentContentFromStorage = async () => {
  await clearAllAttachmentContent();
};
